import { useCallback, useEffect, useState } from "react";

import useService from "../../../../hooks/useService";
import convertKeysToCamelCase from "../../clientNotes/helper/helperFunctions";
import { useEntityNotePermissions } from "../../clientNotes/hooks/useEntityNotePermissions";
import { STRINGS } from "../../clientNotes/utils/strings";
import { getModalHandler } from "../../Common/Modals/hooks/useModalSingleton";

const getSettlementNote = (response) => {
  const normalizedResponse = convertKeysToCamelCase(response);

  if (Array.isArray(normalizedResponse)) {
    return normalizedResponse[0] ?? null;
  }

  if (Array.isArray(normalizedResponse?.results)) {
    return normalizedResponse.results[0] ?? null;
  }

  return normalizedResponse?.id || normalizedResponse?.pk || normalizedResponse?.content
    ? normalizedResponse
    : null;
};

const getSettlementNotePayload = (data) => ({ content: data?.content ?? "" });

export default function useSettlementNotesSection(settlementId) {
  const notesRepository = useService("repositories/settlement-notes-repository");
  const [note, setNote] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { canAddCaseNotes, canEditCaseNotes } = useEntityNotePermissions(note);

  const fetchNote = useCallback(async () => {
    if (!settlementId) {
      setNote(null);
      return;
    }

    setIsLoading(true);
    try {
      const response = await notesRepository.query(settlementId);
      setNote(getSettlementNote(response));
    } catch (error) {
      getModalHandler().openFailureModal(error);
    } finally {
      setIsLoading(false);
    }
  }, [settlementId, notesRepository]);

  useEffect(() => {
    fetchNote();
  }, [fetchNote]);

  const openAdd = () => {
    setMode(STRINGS.ADD_MODE);
    setIsModalOpen(true);
  };

  const openEdit = () => {
    setMode(STRINGS.EDIT_MODE);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setMode(null);
    setIsModalOpen(false);
  };

  const saveNote = async (noteId, data) => {
    const payload = getSettlementNotePayload(data);

    try {
      if (mode === STRINGS.ADD_MODE) {
        await notesRepository.createRecord(settlementId, payload);
      } else {
        await notesRepository.patch(noteId, payload, settlementId);
      }
      closeModal();
      getModalHandler().openSuccessModal();
      fetchNote();
    } catch (error) {
      getModalHandler().openFailureModal(error);
    }
  };

  return {
    canAddNote: canAddCaseNotes,
    canEditNote: canEditCaseNotes,
    closeModal,
    isLoading,
    isModalOpen,
    mode,
    note,
    openAdd,
    openEdit,
    saveNote,
  };
}
